import { client } from "../../hooks/Client";
import { timestamp } from '../../firebase/config';
import callSaveQuestionnaireResponse from './callSaveQuestionnaireResponse';
import hiddenResponsesScope from './hiddenResponsesScope';
import withLinkedStep from '../../helpers/questionnaire/withLinkedStep';

// Persist the collected responses for a single questionnaire field.
// Mirrors the save path in TopLevelFields so questions inside a SECTION persist too
// (the old Section step never saved). Dedupe key = FieldID + session + moment
// (+ RowID for matrix, + Itemid for multiple-multiple) — enforced server-side by
// the saveQuestionnaireResponse Cloud Function; anonymous respondents no longer
// touch Firestore directly. `position` is the navigation index of the step the
// field belongs to; it is stored but deliberately NOT part of the dedup key,
// because a step index shifts when a conditional branch collapses.
// Tags the response with SectionID for section-aware results.
//
// AI-CONTEXT: See docs/features/questionnaire-sections.md

const isMatrixType = (t) => t === 'matrix-one' || t === 'matrix-multiple';

// The CF caps a batch at 50 entries, so chunk.
const BATCH_LIMIT = 50;

/**
 * @param {object} args
 * @param {object} args.field - QuestionnaireFields doc the responses belong to
 * @param {Array<object>} args.fieldResponses - Collected responses for this field
 * @param {number} args.position - Navigation index of the step the field belongs to
 * @param {string} args.questionnaireId - Questionnaires doc ID
 * @param {string} args.moment - MomentID url param
 * @param {string} args.research - ResearchID url param
 * @param {string} args.persona - PersonaID url param ('' when absent)
 * @param {string} args.sessionId - Respondent session uuid
 * @param {Array<string>} [args.hiddenFieldIDs] - Vragen die door een dichtgeklapte
 *   tak verborgen raakten; hun antwoorden worden in dezelfde batch opgeruimd
 * @param {boolean} [args.addressStep] - Het onderzoek vraagt eerst om het adres;
 *   de server zet dan het huishouden op de antwoorden
 * @param {boolean} [args.nameStep] - Het onderzoek vraagt eerst om de naam;
 *   de server zet dan de deelnemer op de antwoorden (zodra die gekoppeld is)
 */
const saveQuestionnaireResponses = async ({
    field,
    fieldResponses,
    position,
    questionnaireId,
    moment,
    research,
    persona,
    sessionId,
    hiddenFieldIDs,
    addressStep,
    nameStep,
}) => {
    const responses = fieldResponses || [];

    // Wezen van een dichtgeklapte tak liften mee op deze aanroep.
    // AI-CONTEXT: See docs/features/conditional-questions.md
    const hiddenScope = hiddenResponsesScope({
        hiddenFieldIDs,
        questionnaireId,
        moment,
        research,
        sessionId,
    });

    // Checkbox questions save one doc per selected option (Itemid identifies
    // the option) and need server-side cleanup of deselected options — so the
    // CF must be called even when nothing is checked. The `field` scope tells
    // it which same-session docs may be removed (Itemid not in KeepItemids).
    const isMultiMulti = field.Type === 'multiple-multiple';

    if (responses.length === 0 && !isMultiMulti && !hiddenScope) return;

    const items = responses.map((fieldResponse) => ({
        isMatrix: isMatrixType(fieldResponse?.FieldType),
        response: {
            FieldID: field.ID,
            FieldTitle: field.Question,
            // ?? en niet ||: het antwoord 0 is een antwoord (#314).
            // AI-CONTEXT: See docs/pitfalls.md#state-or-fallback-erases-zero
            Input: fieldResponse?.Input ?? '',
            CompagnyID: client,
            QuestionannaireID: questionnaireId,
            MomentID: moment,
            ResearchID: research,
            FormID: sessionId,
            SessionID: sessionId,
            OptionType: fieldResponse?.OptionType || '',
            Persona: persona || '',
            RowTitle: fieldResponse?.RowTitle || '',
            RowTitleRight: fieldResponse?.RowTitleRight || '',
            RowID: fieldResponse?.RowID || '',
            FieldType: fieldResponse?.FieldType || '',
            ColumnID: fieldResponse?.ColumnID || '',
            Itemid: fieldResponse?.Itemid || '',
            SectionID: field.SectionID || '',
            Position: position,
            // Plek op de schuifbalk; alleen als die er was. AI-CONTEXT: See docs/features/vas-slider.md
            ...(typeof fieldResponse?.VasRaw === 'number' ? { VasRaw: fieldResponse.VasRaw } : {}),
        },
    }));

    const cleanupScope = isMultiMulti
        ? {
            FieldID: field.ID,
            CompagnyID: client,
            QuestionannaireID: questionnaireId,
            MomentID: moment,
            ResearchID: research,
            SessionID: sessionId,
            Position: position,
            FieldType: 'multiple-multiple',
            KeepItemids: responses.map(r => r?.Itemid).filter(Boolean),
        }
        : undefined;

    if (items.length === 0) {
        // Everything deselected: no upserts, cleanup only
        await callSaveQuestionnaireResponse(withLinkedStep({
            timestampMillis: timestamp.toMillis(),
            responses: [],
            field: cleanupScope,
            ...(hiddenScope ? { hidden: hiddenScope } : {}),
        }, { addressStep, nameStep }));
        return;
    }

    for (let start = 0; start < items.length; start += BATCH_LIMIT) {
        await callSaveQuestionnaireResponse(withLinkedStep({
            timestampMillis: timestamp.toMillis(),
            responses: items.slice(start, start + BATCH_LIMIT),
            // Cleanup runs once, on the first chunk; KeepItemids covers the full
            // selection so later chunks' docs are never treated as deselected.
            ...(start === 0 && cleanupScope ? { field: cleanupScope } : {}),
            ...(start === 0 && hiddenScope ? { hidden: hiddenScope } : {}),
        }, { addressStep, nameStep }));
    }
};

export default saveQuestionnaireResponses;
